import { CircuitResponse } from '@/types/circuits/CircuitResponse';
import { addMultiDaySteps, parseSteps } from '../loadCircuitDefaultValues';
import { getCircuitDaysWithMultiDayBenefitTracking } from './getCircuitDaysWithMultiDayBenefitTracking';
import {
  getCircuitDayPricesObject,
  getCircuitDayTotalPrice,
} from './computeBenefitPrices';
import { addDays } from 'date-fns';
import { roundUpPrice } from './formatPrice';
import { getPricingStartDates } from './pricingHorizon';
import { Translator } from '@/types/translator.type';

export const computeCircuitStartingPrice = ({
  circuitDays,
  nbParticipants,
  circuitMarginRate = 0,
  nbChildren = 0,
  t,
}: {
  circuitDays: Omit<
    CircuitResponse['circuitDays'][number],
    'id' | 'circuitId' | 'stepTitle' | 'images' | 'description'
  >[];
  nbParticipants: number;
  circuitMarginRate?: number;
  nbChildren?: number;
  t: Translator;
}) => {
  if (nbParticipants <= 0) return 0;

  const parsedCircuitDays = addMultiDaySteps(
    circuitDays.toSorted((a, b) => a.day - b.day),
  ).map((circuitDay) => ({
    steps:
      circuitDay.CircuitDayStep && circuitDay.CircuitDayStep.length
        ? parseSteps(circuitDay.CircuitDayStep, undefined, t)
        : [],
  }));

  const daysTrackingMultiDayBenefits =
    getCircuitDaysWithMultiDayBenefitTracking(parsedCircuitDays);

  const circuitDaysObject = daysTrackingMultiDayBenefits.map((day, indexDay) =>
    getCircuitDayPricesObject({
      circuitDay: day,
      nbParticipants: nbParticipants,
      circuitMarginRate: circuitMarginRate
        ? Number(circuitMarginRate)
        : undefined,
      nbChildren,
      indexDay,
    }),
  );

  const circuitDaysPriceByStartDate = (startDate: Date) =>
    circuitDaysObject.map((circuitDay, indexDay) =>
      getCircuitDayTotalPrice({
        circuitDayPrices: circuitDay,
        day: addDays(startDate, indexDay),
      }),
    );

  const findMinimumPriceForDate = (date: Date) =>
    circuitDaysPriceByStartDate(date).reduce((a, b) => a + b, 0);

  const pricesByStartDate = new Map(
    getPricingStartDates().map((date) => [
      date.toISOString(),
      findMinimumPriceForDate(date),
    ]),
  );

  return roundUpPrice(
    Array.from(pricesByStartDate.values())
      .filter((price) => price > 0)
      .reduce((a, b) => Math.min(a, b), Infinity),
  );
};
